import React, { useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Search, Copy, Check, Lock, Globe, Server, KeyRound } from "lucide-react";

const METHOD_STYLES = {
  GET: "bg-purple-50 text-purple-700 border-purple-200",
  POST: "bg-purple-100 text-purple-700 border-purple-200",
  PUT: "bg-purple-50 text-purple-700 border-purple-200",
  PATCH: "bg-purple-100 text-purple-800 border-purple-200",
  DELETE: "bg-danger/15 text-danger border-danger/30",
};

function CopyBlock({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="relative group">
      <pre className="bg-ink text-white text-xs leading-relaxed p-4 rounded-md overflow-x-auto font-mono whitespace-pre-wrap break-all">
        {text}
      </pre>
      <button
        onClick={handleCopy}
        data-testid="copy-code-button"
        className="absolute top-2 right-2 p-1.5 rounded bg-ink-soft hover:bg-ink-soft text-white/70 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
      </button>
    </div>
  );
}

function MethodBadge({ method }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold tracking-wider border ${METHOD_STYLES[method] || ""}`}>
      {method}
    </span>
  );
}

function AuthBadge({ type }) {
  if (type === "jwt") return (
    <span className="inline-flex items-center gap-1 text-[10px] text-ink bg-purple-50 border border-warning/40 px-1.5 py-0.5 rounded font-medium">
      <Lock size={9} /> JWT Bearer
    </span>
  );
  if (type === "apikey") return (
    <span className="inline-flex items-center gap-1 text-[10px] text-purple-700 bg-purple-100 border border-purple-200 px-1.5 py-0.5 rounded font-medium">
      <Server size={9} /> X-API-Key
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-ink/50 bg-purple-50 border border-ink/10 px-1.5 py-0.5 rounded font-medium">
      <Globe size={9} /> Public
    </span>
  );
}

function Field({ name, type, required, desc }) {
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-ink/5 last:border-0">
      <code className="text-xs font-mono text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded shrink-0">{name}</code>
      <span className="text-[10px] text-ink/40 font-mono shrink-0">{type}</span>
      {required && <span className="text-[10px] text-danger font-semibold shrink-0">required</span>}
      <span className="text-xs text-ink/50 leading-snug">{desc}</span>
    </div>
  );
}

/* ── Authentication overview (JWT + API Key) ── */
function AuthOverview() {
  return (
    <div className="grid md:grid-cols-2 gap-4 mb-8" data-testid="auth-overview">
      {/* JWT */}
      <div className="bg-white border border-ink/10 rounded-md shadow-sm p-5" data-testid="auth-jwt-card">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center"><Lock size={15} className="text-ink" /></div>
          <h3 className="text-sm font-semibold text-ink">JWT Bearer — Dashboard</h3>
        </div>
        <p className="text-xs text-ink/55 leading-relaxed mb-3">
          For the admin &amp; vendor dashboard. Call <code className="bg-purple-50 px-1 rounded">/api/auth/login</code> to
          get a 60-minute <strong>access token</strong> + 7-day <strong>refresh token</strong>, then send it on every dashboard request.
        </p>
        <CopyBlock text={`Authorization: Bearer <access_token>`} />
      </div>
      {/* API Key */}
      <div className="bg-white border border-ink/10 rounded-md shadow-sm p-5" data-testid="auth-apikey-card">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center"><KeyRound size={15} className="text-purple-700" /></div>
          <h3 className="text-sm font-semibold text-ink">X-API-Key — POS / Server</h3>
        </div>
        <p className="text-xs text-ink/55 leading-relaxed mb-3">
          For point-of-sale &amp; server-to-server calls to the Loyalty API. Each vendor has a unique key
          (<code className="bg-purple-50 px-1 rounded">cl_…</code> prefix) found under <strong>Settings → API Key</strong>.
          Send it in the <code className="bg-purple-50 px-1 rounded">X-API-Key</code> header — no login required.
        </p>
        <CopyBlock text={`X-API-Key: cl_your_api_key_here`} />
      </div>
    </div>
  );
}

const ENDPOINT_GROUPS = [
  {
    title: "Authentication",
    description: "JWT auth for the dashboard. Vendors self-register and are approved by an admin; admins are seeded.",
    endpoints: [
      {
        method: "POST", path: "/api/auth/register", summary: "Vendor self-registration", auth: "public",
        description: "Register a new vendor account. Created with is_approved=false — an admin must approve before login is allowed.",
        requestBody: [
          { name: "email", type: "string", required: true, desc: "Vendor email" },
          { name: "password", type: "string", required: true, desc: "Account password" },
          { name: "name", type: "string", required: true, desc: "Contact / business name" },
        ],
        responseExample: `{ "success": true, "message": "Registration submitted. Pending admin approval." }`,
        curlExample: `curl -X POST {BASE}/api/auth/register \\
  -H "Content-Type: application/json" \\
  -d '{"email":"owner@shop.com","password":"Secret123","name":"My Shop"}'`,
      },
      {
        method: "POST", path: "/api/auth/login", summary: "Login (admin or vendor)", auth: "public",
        description: "Authenticate with email + password. Returns a 60-min access token and 7-day refresh token. Unapproved vendors are rejected with 403.",
        requestBody: [
          { name: "email", type: "string", required: true, desc: "Account email" },
          { name: "password", type: "string", required: true, desc: "Account password" },
        ],
        responseExample: `{
  "access_token": "eyJhbG...",
  "refresh_token": "eyJhbG...",
  "token_type": "bearer",
  "user": { "id": "...", "email": "...", "name": "...", "role": "vendor", "vendor_id": "...", "is_approved": true }
}`,
        curlExample: `curl -X POST {BASE}/api/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{"email":"admin@loyaltyengine.com","password":"Admin@123456"}'`,
      },
      {
        method: "GET", path: "/api/auth/me", summary: "Current user", auth: "jwt",
        description: "Returns the authenticated user's profile (includes role and vendor_id).",
        responseExample: `{ "id": "...", "email": "...", "name": "...", "role": "vendor", "vendor_id": "...", "is_approved": true }`,
        curlExample: `curl -X GET {BASE}/api/auth/me -H "Authorization: Bearer <access_token>"`,
      },
      {
        method: "POST", path: "/api/auth/refresh", summary: "Refresh access token", auth: "public",
        description: "Exchange a refresh token (Authorization header or cookie) for a new access token.",
        responseExample: `{ "access_token": "eyJhbG...", "token_type": "bearer" }`,
        curlExample: `curl -X POST {BASE}/api/auth/refresh -H "Authorization: Bearer <refresh_token>"`,
      },
      {
        method: "GET", path: "/api/auth/pending", summary: "List pending vendors (admin)", auth: "jwt",
        description: "Admin only. Returns vendors awaiting approval.",
        curlExample: `curl -X GET {BASE}/api/auth/pending -H "Authorization: Bearer <admin_token>"`,
      },
      {
        method: "POST", path: "/api/auth/approve/:user_id", summary: "Approve a vendor (admin)", auth: "jwt",
        description: "Admin only. Approves a pending vendor so they can log in.",
        curlExample: `curl -X POST {BASE}/api/auth/approve/<user_id> -H "Authorization: Bearer <admin_token>"`,
      },
      {
        method: "POST", path: "/api/auth/reject/:user_id", summary: "Reject a vendor (admin)", auth: "jwt",
        description: "Admin only. Deletes a pending vendor registration.",
        curlExample: `curl -X POST {BASE}/api/auth/reject/<user_id> -H "Authorization: Bearer <admin_token>"`,
      },
    ],
  },
  {
    title: "Vendors",
    description: "Manage business vendors (multi-tenant). Each vendor owns a unique API key for POS integration, branding and reward config.",
    endpoints: [
      {
        method: "GET", path: "/api/vendors", summary: "List vendors (admin)", auth: "jwt",
        description: "Admin only. Returns all vendors with customer counts.",
        curlExample: `curl -X GET {BASE}/api/vendors -H "Authorization: Bearer <admin_token>"`,
      },
      {
        method: "POST", path: "/api/vendors", summary: "Create a vendor (admin)", auth: "jwt",
        description: "Admin only. Registers a vendor and auto-generates an API key (cl_ prefix).",
        requestBody: [
          { name: "name", type: "string", required: true, desc: "Business name" },
          { name: "industry", type: "string", required: false, desc: "Industry type" },
          { name: "reward_logic", type: "string", required: false, desc: "spend_based | item_based | order_based | tier_based" },
          { name: "points_per_pound", type: "number", required: false, desc: "Points per £1 (default 10)" },
          { name: "reward_threshold", type: "number", required: false, desc: "Points needed for a reward" },
        ],
        responseExample: `{ "id": "...", "name": "Coffee House", "slug": "coffee-house", "api_key": "cl_a1b2c3...", "reward_logic": "spend_based" }`,
        curlExample: `curl -X POST {BASE}/api/vendors \\
  -H "Authorization: Bearer <admin_token>" -H "Content-Type: application/json" \\
  -d '{"name":"Coffee House","reward_logic":"spend_based","points_per_pound":10}'`,
      },
      {
        method: "POST", path: "/api/vendors/setup", summary: "Vendor onboarding", auth: "jwt",
        description: "Called by a freshly-approved vendor to create their vendor record and link it to their user account.",
        requestBody: [
          { name: "name", type: "string", required: true, desc: "Business name" },
          { name: "industry", type: "string", required: false, desc: "Industry type" },
          { name: "reward_logic", type: "string", required: false, desc: "Reward system type" },
        ],
        curlExample: `curl -X POST {BASE}/api/vendors/setup \\
  -H "Authorization: Bearer <vendor_token>" -H "Content-Type: application/json" \\
  -d '{"name":"My Shop","industry":"food_beverage","reward_logic":"order_based"}'`,
      },
      {
        method: "GET", path: "/api/vendors/me", summary: "My vendor", auth: "jwt",
        description: "Returns the vendor record linked to the authenticated vendor user.",
        curlExample: `curl -X GET {BASE}/api/vendors/me -H "Authorization: Bearer <vendor_token>"`,
      },
      {
        method: "GET", path: "/api/vendors/:id", summary: "Vendor details", auth: "jwt",
        description: "Returns full vendor configuration (branding, reward config, wallet_pass_config, timezone, stamp settings).",
        curlExample: `curl -X GET {BASE}/api/vendors/<vendor_id> -H "Authorization: Bearer <access_token>"`,
      },
      {
        method: "PUT", path: "/api/vendors/:id", summary: "Update a vendor", auth: "jwt",
        description: "Partial update. Includes reward config, stamp controls and branding.",
        requestBody: [
          { name: "name", type: "string", required: false, desc: "Business name (regenerates slug)" },
          { name: "reward_logic", type: "string", required: false, desc: "Reward system type" },
          { name: "stamp_rewards", type: "array", required: false, desc: "Stamp milestones: [{orders_required, reward_id}]" },
          { name: "stamp_min_spend", type: "number", required: false, desc: "Min qualifying spend per stamp" },
          { name: "stamp_max_per_day", type: "number", required: false, desc: "Max stamps per day (0 = no limit)" },
          { name: "stamp_auto_reset", type: "boolean", required: false, desc: "Reset stamps after final milestone" },
          { name: "tier_config", type: "array", required: false, desc: "Tiers: [{name, min_points, reward_id, perks}]" },
          { name: "reward_threshold_reward_id", type: "string", required: false, desc: "Catalog reward issued at the points threshold" },
          { name: "timezone", type: "string", required: false, desc: "IANA timezone for earning-rule schedules" },
          { name: "brand_color", type: "string", required: false, desc: "Card background hex" },
          { name: "allow_customer_signup", type: "boolean", required: false, desc: "Enable the /:slug/join portal" },
        ],
        curlExample: `curl -X PUT {BASE}/api/vendors/<vendor_id> \\
  -H "Authorization: Bearer <access_token>" -H "Content-Type: application/json" \\
  -d '{"points_per_pound":15,"timezone":"Europe/London"}'`,
      },
      {
        method: "DELETE", path: "/api/vendors/:id", summary: "Delete a vendor (admin)", auth: "jwt",
        description: "Admin only. Permanently removes a vendor.",
        curlExample: `curl -X DELETE {BASE}/api/vendors/<vendor_id> -H "Authorization: Bearer <admin_token>"`,
      },
      {
        method: "PUT", path: "/api/vendors/:id/client-id", summary: "Set custom client ID", auth: "jwt",
        description: "Set a friendly custom client_id (used in API calls / portal links). Must be unique.",
        requestBody: [{ name: "custom_client_id", type: "string", required: true, desc: "Friendly slug, e.g. my-shop" }],
        curlExample: `curl -X PUT {BASE}/api/vendors/<vendor_id>/client-id \\
  -H "Authorization: Bearer <access_token>" -H "Content-Type: application/json" \\
  -d '{"custom_client_id":"my-shop"}'`,
      },
      {
        method: "POST", path: "/api/vendors/:id/regenerate-key", summary: "Regenerate API key", auth: "jwt",
        description: "Generates a new X-API-Key. The previous key is immediately invalidated.",
        responseExample: `{ "api_key": "cl_new_key_here..." }`,
        curlExample: `curl -X POST {BASE}/api/vendors/<vendor_id>/regenerate-key -H "Authorization: Bearer <access_token>"`,
      },
      {
        method: "GET", path: "/api/vendors/:id/api-logs", summary: "Vendor API logs", auth: "jwt",
        description: "Paginated log of loyalty API calls (collect-points, redeem, onboard) made with this vendor's API key.",
        requestBody: [
          { name: "page", type: "number", required: false, desc: "Page number (default 1)" },
          { name: "limit", type: "number", required: false, desc: "Items per page (default 20, max 100)" },
        ],
        curlExample: `curl -X GET "{BASE}/api/vendors/<vendor_id>/api-logs?page=1&limit=20" -H "Authorization: Bearer <access_token>"`,
      },
      {
        method: "GET", path: "/api/vendors/:id/value-check", summary: "10% Value Validator", auth: "jwt",
        description: "Checks if the reward is worth at least 10% of the required spend. Flags low-value configs that risk disengagement.",
        responseExample: `{ "reward_value_gbp": 5.0, "required_spend_gbp": 50.0, "ratio_percent": 10.0, "is_meaningful": true, "status": "Healthy" }`,
        curlExample: `curl -X GET {BASE}/api/vendors/<vendor_id>/value-check -H "Authorization: Bearer <access_token>"`,
      },
      {
        method: "POST", path: "/api/vendors/:id/upload-logo", summary: "Upload logo / banner", auth: "jwt",
        description: "Multipart upload of a vendor logo. logo_type query param: header (banner) or icon (square).",
        curlExample: `curl -X POST "{BASE}/api/vendors/<vendor_id>/upload-logo?logo_type=icon" \\
  -H "Authorization: Bearer <access_token>" -F "file=@logo.png"`,
      },
      {
        method: "GET", path: "/api/vendors/:id/logo/:logo_type", summary: "Fetch logo image", auth: "public",
        description: "Returns the raw logo image (header | icon). Used by the portal and wallet passes.",
        curlExample: `curl -X GET {BASE}/api/vendors/<vendor_id>/logo/icon`,
      },
      {
        method: "POST", path: "/api/vendors/:id/push-all", summary: "Refresh all wallet passes", auth: "jwt",
        description: "Force an Apple Wallet pass refresh for all of this vendor's registered devices.",
        curlExample: `curl -X POST {BASE}/api/vendors/<vendor_id>/push-all -H "Authorization: Bearer <access_token>"`,
      },
    ],
  },
  {
    title: "Customers",
    description: "Manage loyalty customers. Identified by email or mobile number; phone-only customers are created as shadow profiles.",
    endpoints: [
      {
        method: "GET", path: "/api/customers", summary: "List / search customers", auth: "jwt",
        description: "Paginated customer list with optional search (email, mobile, name) and client filter. Vendors are auto-scoped to their own customers.",
        requestBody: [
          { name: "search", type: "string", required: false, desc: "Search by email, mobile, or name" },
          { name: "client_id", type: "string", required: false, desc: "Filter by vendor (admins)" },
          { name: "page", type: "number", required: false, desc: "Page number" },
          { name: "limit", type: "number", required: false, desc: "Items per page (max 100)" },
        ],
        responseExample: `{
  "customers": [
    { "id": "...", "name": "John Doe", "email": "john@example.com", "mobile_number": "+447700900000",
      "point_balance": 340, "total_points_earned": 1200, "group_ids": ["..."], "vendor_name": "Coffee House" }
  ],
  "total": 85, "page": 1, "limit": 20
}`,
        curlExample: `curl -X GET "{BASE}/api/customers?search=john&page=1" -H "Authorization: Bearer <access_token>"`,
      },
      {
        method: "GET", path: "/api/customers/:id", summary: "Customer detail", auth: "jwt",
        description: "Full customer profile with recent transactions and active rewards.",
        curlExample: `curl -X GET {BASE}/api/customers/<customer_id> -H "Authorization: Bearer <access_token>"`,
      },
      {
        method: "PUT", path: "/api/customers/:id", summary: "Update customer", auth: "jwt",
        description: "Partial update (name, email, mobile).",
        curlExample: `curl -X PUT {BASE}/api/customers/<customer_id> \\
  -H "Authorization: Bearer <access_token>" -H "Content-Type: application/json" -d '{"name":"Jane Doe"}'`,
      },
      {
        method: "POST", path: "/api/customers/:id/adjust-points", summary: "Manually adjust points", auth: "jwt",
        description: "Add or remove points for a customer (e.g. goodwill, corrections).",
        requestBody: [
          { name: "points", type: "number", required: true, desc: "Positive to add, negative to deduct" },
          { name: "reason", type: "string", required: false, desc: "Audit note" },
        ],
        curlExample: `curl -X POST {BASE}/api/customers/<customer_id>/adjust-points \\
  -H "Authorization: Bearer <access_token>" -H "Content-Type: application/json" -d '{"points":100,"reason":"Goodwill"}'`,
      },
      {
        method: "POST", path: "/api/customers/:id/issue-reward", summary: "Issue a manual reward", auth: "jwt",
        description: "Manually issue a reward to a customer. Triggers a 'reward issued' transactional email if configured.",
        requestBody: [
          { name: "value_gbp", type: "number", required: true, desc: "Reward face value in GBP" },
          { name: "label", type: "string", required: false, desc: "Reward label" },
        ],
        responseExample: `{ "success": true, "reward_id": "...", "value_gbp": 5.0 }`,
        curlExample: `curl -X POST {BASE}/api/customers/<customer_id>/issue-reward \\
  -H "Authorization: Bearer <access_token>" -H "Content-Type: application/json" -d '{"value_gbp":5,"label":"Free Coffee"}'`,
      },
      {
        method: "POST", path: "/api/customers/:id/notify", summary: "Send a personal notification", auth: "jwt",
        description: "Send a notification to one customer across FCM (web push), Apple Wallet, and Google Wallet. Same channels as loyalty activity auto-notify.",
        requestBody: [
          { name: "title", type: "string", required: true, desc: "Notification title" },
          { name: "body", type: "string", required: true, desc: "Notification body" },
          { name: "url", type: "string", required: false, desc: "Optional FCM deep-link URL" },
        ],
        responseExample: `{ "success": true, "customer_id": "...", "fcm": { "sent": 1 }, "apns": { "sent": 1 }, "google": { "updated": 1 } }`,
        curlExample: `curl -X POST {BASE}/api/customers/<customer_id>/notify \\
  -H "Authorization: Bearer <access_token>" -H "Content-Type: application/json" \\
  -d '{"title":"VIP Offer","body":"20% off today only"}'`,
      },
    ],
  },
  {
    title: "Customer Groups",
    description: "Organise customers into groups for targeted nudges. Membership is stored as group_ids on each customer.",
    endpoints: [
      {
        method: "GET", path: "/api/customer-groups/:vendor_id", summary: "List groups", auth: "jwt",
        description: "Returns all groups for a vendor with member counts.",
        responseExample: `[ { "id": "...", "vendor_id": "...", "name": "VIPs", "member_count": 12 } ]`,
        curlExample: `curl -X GET {BASE}/api/customer-groups/<vendor_id> -H "Authorization: Bearer <access_token>"`,
      },
      {
        method: "POST", path: "/api/customer-groups/:vendor_id", summary: "Create a group", auth: "jwt",
        description: "Create a new customer group.",
        requestBody: [{ name: "name", type: "string", required: true, desc: "Group name" }],
        responseExample: `{ "id": "...", "name": "VIPs", "member_count": 0 }`,
        curlExample: `curl -X POST {BASE}/api/customer-groups/<vendor_id> \\
  -H "Authorization: Bearer <access_token>" -H "Content-Type: application/json" -d '{"name":"VIPs"}'`,
      },
      {
        method: "PUT", path: "/api/customer-groups/:vendor_id/:group_id", summary: "Rename a group", auth: "jwt",
        description: "Rename an existing group.",
        requestBody: [{ name: "name", type: "string", required: true, desc: "New group name" }],
        curlExample: `curl -X PUT {BASE}/api/customer-groups/<vendor_id>/<group_id> \\
  -H "Authorization: Bearer <access_token>" -H "Content-Type: application/json" -d '{"name":"Top Spenders"}'`,
      },
      {
        method: "DELETE", path: "/api/customer-groups/:vendor_id/:group_id", summary: "Delete a group", auth: "jwt",
        description: "Delete a group and remove it from all member customers (customers are not deleted).",
        curlExample: `curl -X DELETE {BASE}/api/customer-groups/<vendor_id>/<group_id> -H "Authorization: Bearer <access_token>"`,
      },
      {
        method: "GET", path: "/api/customer-groups/:vendor_id/:group_id/members", summary: "List group members", auth: "jwt",
        description: "Returns the customers belonging to a group.",
        curlExample: `curl -X GET {BASE}/api/customer-groups/<vendor_id>/<group_id>/members -H "Authorization: Bearer <access_token>"`,
      },
      {
        method: "POST", path: "/api/customer-groups/:vendor_id/:group_id/members", summary: "Add members", auth: "jwt",
        description: "Add one or more customers to a group.",
        requestBody: [{ name: "customer_ids", type: "string[]", required: true, desc: "Customer IDs to add" }],
        responseExample: `{ "success": true, "added": 3 }`,
        curlExample: `curl -X POST {BASE}/api/customer-groups/<vendor_id>/<group_id>/members \\
  -H "Authorization: Bearer <access_token>" -H "Content-Type: application/json" -d '{"customer_ids":["id1","id2"]}'`,
      },
      {
        method: "DELETE", path: "/api/customer-groups/:vendor_id/:group_id/members/:customer_id", summary: "Remove a member", auth: "jwt",
        description: "Remove a single customer from a group.",
        curlExample: `curl -X DELETE {BASE}/api/customer-groups/<vendor_id>/<group_id>/members/<customer_id> -H "Authorization: Bearer <access_token>"`,
      },
    ],
  },
  {
    title: "Transactions",
    description: "Audit log of all earn and redeem events across the platform.",
    endpoints: [
      {
        method: "GET", path: "/api/transactions", summary: "List transactions", auth: "jwt",
        description: "Paginated transaction log with filters for client, customer, store, type and date range.",
        requestBody: [
          { name: "client_id", type: "string", required: false, desc: "Filter by vendor" },
          { name: "customer_id", type: "string", required: false, desc: "Filter by customer" },
          { name: "tx_type", type: "string", required: false, desc: "earn | redeem" },
          { name: "date_from", type: "string", required: false, desc: "ISO date (YYYY-MM-DD)" },
          { name: "date_to", type: "string", required: false, desc: "ISO date (YYYY-MM-DD)" },
          { name: "page", type: "number", required: false, desc: "Page number" },
          { name: "limit", type: "number", required: false, desc: "Items per page" },
        ],
        curlExample: `curl -X GET "{BASE}/api/transactions?tx_type=earn&page=1" -H "Authorization: Bearer <access_token>"`,
      },
    ],
  },
  {
    title: "Loyalty API (POS Integration)",
    description: "Core headless API for point-of-sale & server integrations. Authenticated with the per-vendor X-API-Key header (no login). Covers earning/redeeming points plus customer management (edit details, adjust points, issue rewards) — customers are addressed by email or mobile.",
    endpoints: [
      {
        method: "POST", path: "/api/loyalty/collect-points", summary: "Collect points from a transaction", auth: "apikey",
        description: "Submit a transaction to earn points/stamps. Supports spend_based, item_based, order_based (stamp card) and tier_based accrual, applies active Earning Rules, and auto-issues catalog rewards when thresholds/milestones/tiers are reached. Auto-creates shadow profiles for unknown customers.",
        requestBody: [
          { name: "identifier", type: "string", required: true, desc: "Customer email or mobile" },
          { name: "total", type: "number", required: true, desc: "Transaction amount in GBP" },
          { name: "store_id", type: "string", required: false, desc: "POS store identifier" },
          { name: "store_location", type: "string", required: false, desc: "Store address or region" },
          { name: "item_list", type: "array", required: false, desc: "For item_based: [{category, qty}]" },
          { name: "channel", type: "string", required: false, desc: "App | Web | Kiosk | SMS (for Earning Rule channel gating)" },
        ],
        responseExample: `{
  "success": true,
  "points_earned": 245,
  "base_points": 245,
  "applied_rules": [],
  "new_balance": 745,
  "total_points_earned": 1245,
  "reward_issued": true,
  "reward_id": "...",
  "reward_value_gbp": 5.0,
  "tier_changed": null,
  "customer_id": "..."
}`,
        curlExample: `curl -X POST {BASE}/api/loyalty/collect-points \\
  -H "X-API-Key: cl_your_api_key_here" -H "Content-Type: application/json" \\
  -d '{"identifier":"john@email.com","total":24.50,"store_id":"STORE-001","channel":"App"}'`,
      },
      {
        method: "GET", path: "/api/loyalty/checkout-summary", summary: "POS checkout summary", auth: "apikey",
        description: "Real-time customer lookup for POS displays — tier, available rewards, balance and progress to next reward.",
        requestBody: [{ name: "identifier", type: "string", required: true, desc: "Customer email or mobile (query param)" }],
        responseExample: `{
  "customer_id": "...", "customer_name": "John Doe", "reward_logic": "tier_based",
  "point_balance": 340, "total_points_earned": 1200,
  "available_rewards": [ { "id": "...", "value_gbp": 5.0, "label": "Silver Tier Reward" } ],
  "tier": { "current_name": "Silver", "next_name": "Gold", "points_to_next_tier": 800, "progress_to_next_tier_percent": 60.0 },
  "progress_percent": 60.0
}`,
        curlExample: `curl -X GET "{BASE}/api/loyalty/checkout-summary?identifier=john@email.com" -H "X-API-Key: cl_your_api_key_here"`,
      },
      {
        method: "POST", path: "/api/loyalty/redeem", summary: "Redeem a reward", auth: "apikey",
        description: "Redeem an issued reward at the POS. Validates the reward hasn't expired or been redeemed.",
        requestBody: [
          { name: "identifier", type: "string", required: true, desc: "Customer email or mobile" },
          { name: "reward_id", type: "string", required: true, desc: "The reward ID to redeem" },
        ],
        responseExample: `{ "success": true, "reward_id": "...", "value_gbp": 5.0 }`,
        curlExample: `curl -X POST {BASE}/api/loyalty/redeem \\
  -H "X-API-Key: cl_your_api_key_here" -H "Content-Type: application/json" \\
  -d '{"identifier":"john@email.com","reward_id":"<reward_id>"}'`,
      },
      {
        method: "POST", path: "/api/loyalty/onboard", summary: "Onboard a customer", auth: "apikey",
        description: "Register a new customer or upgrade a shadow profile to a full profile. Idempotent.",
        requestBody: [
          { name: "identifier", type: "string", required: true, desc: "Customer email or mobile" },
          { name: "name", type: "string", required: false, desc: "Customer full name" },
          { name: "email", type: "string", required: false, desc: "Email (if identifier is mobile)" },
          { name: "mobile_number", type: "string", required: false, desc: "Mobile (if identifier is email)" },
        ],
        responseExample: `{ "success": true, "customer": { "id": "...", "name": "John Doe", "email": "john@email.com", "point_balance": 0, "is_shadow_profile": false } }`,
        curlExample: `curl -X POST {BASE}/api/loyalty/onboard \\
  -H "X-API-Key: cl_your_api_key_here" -H "Content-Type: application/json" \\
  -d '{"identifier":"john@email.com","name":"John Doe","mobile_number":"+447700900000"}'`,
      },
      {
        method: "PUT", path: "/api/loyalty/customer", summary: "Edit customer details", auth: "apikey",
        description: "Update a customer's profile (name, email, mobile, DOB) from the POS / server. Customer is found by identifier. Returns 404 if not found.",
        requestBody: [
          { name: "identifier", type: "string", required: true, desc: "Customer email or mobile (lookup key)" },
          { name: "name", type: "string", required: false, desc: "New name" },
          { name: "email", type: "string", required: false, desc: "New email" },
          { name: "mobile_number", type: "string", required: false, desc: "New mobile number" },
          { name: "date_of_birth", type: "string", required: false, desc: "YYYY-MM-DD" },
        ],
        responseExample: `{ "success": true, "customer": { "id": "...", "name": "Jane Doe", "email": "jane@email.com", "date_of_birth": "1990-05-01", "point_balance": 150 } }`,
        curlExample: `curl -X PUT {BASE}/api/loyalty/customer \\
  -H "X-API-Key: cl_your_api_key_here" -H "Content-Type: application/json" \\
  -d '{"identifier":"john@email.com","name":"Jane Doe","date_of_birth":"1990-05-01"}'`,
      },
      {
        method: "POST", path: "/api/loyalty/adjust-points", summary: "Adjust points", auth: "apikey",
        description: "Add or deduct points for a customer (positive adds, negative deducts; balance floored at 0). Logs a manual transaction and refreshes the wallet pass. Returns 404 if the customer is not found.",
        requestBody: [
          { name: "identifier", type: "string", required: true, desc: "Customer email or mobile" },
          { name: "points", type: "number", required: true, desc: "Positive to add, negative to deduct" },
          { name: "reason", type: "string", required: false, desc: "Audit note" },
        ],
        responseExample: `{ "success": true, "new_balance": 150, "points_adjusted": 150 }`,
        curlExample: `curl -X POST {BASE}/api/loyalty/adjust-points \\
  -H "X-API-Key: cl_your_api_key_here" -H "Content-Type: application/json" \\
  -d '{"identifier":"john@email.com","points":150,"reason":"Goodwill"}'`,
      },
      {
        method: "POST", path: "/api/loyalty/issue-reward", summary: "Issue a reward", auth: "apikey",
        description: "Issue a reward to a customer. Provide either reward_id (a catalog reward) OR an inline reward (value_gbp + optional label/reward_type/discount_percent). Sends the 'reward issued' email and refreshes the wallet pass. 404 if customer not found; 400 if neither reward_id nor value_gbp is given.",
        requestBody: [
          { name: "identifier", type: "string", required: true, desc: "Customer email or mobile" },
          { name: "reward_id", type: "string", required: false, desc: "Catalog reward ID (preferred)" },
          { name: "value_gbp", type: "number", required: false, desc: "Inline reward value (if no reward_id)" },
          { name: "label", type: "string", required: false, desc: "Inline reward label" },
          { name: "reward_type", type: "string", required: false, desc: "money | item | discount (inline)" },
          { name: "discount_percent", type: "number", required: false, desc: "For discount rewards" },
        ],
        responseExample: `{ "success": true, "reward_id": "...", "label": "Free Coffee", "value_gbp": 3.5 }`,
        curlExample: `curl -X POST {BASE}/api/loyalty/issue-reward \\
  -H "X-API-Key: cl_your_api_key_here" -H "Content-Type: application/json" \\
  -d '{"identifier":"john@email.com","reward_id":"<catalog_reward_id>"}'`,
      },
      {
        method: "POST", path: "/api/loyalty/notify", summary: "Notify a single customer", auth: "apikey",
        description: "Send a notification to one customer across FCM (web push), Apple Wallet (APNs pass refresh with changeMessage), and Google Wallet (addMessage TEXT_AND_NOTIFY). Identify by email or mobile. Returns 404 if the customer is not found (does not auto-create).",
        requestBody: [
          { name: "identifier", type: "string", required: true, desc: "Customer email or mobile" },
          { name: "title", type: "string", required: true, desc: "Notification title" },
          { name: "body", type: "string", required: true, desc: "Notification body" },
          { name: "url", type: "string", required: false, desc: "Optional FCM deep-link URL" },
        ],
        responseExample: `{ "success": true, "customer_id": "...", "fcm": { "sent": 1 }, "apns": { "sent": 1 }, "google": { "updated": 1 } }`,
        curlExample: `curl -X POST {BASE}/api/loyalty/notify \\
  -H "X-API-Key: cl_your_api_key_here" -H "Content-Type: application/json" \\
  -d '{"identifier":"john@email.com","title":"VIP Offer","body":"20% off today only"}'`,
      },
    ],
  },
  {
    title: "Rewards Catalog",
    description: "Reusable reward definitions a vendor attaches to stamp milestones, tiers or point thresholds.",
    endpoints: [
      {
        method: "GET", path: "/api/rewards/:vendor_id", summary: "List catalog rewards", auth: "jwt",
        description: "Returns the vendor's reusable reward catalog.",
        responseExample: `[ { "id": "...", "name": "Free Coffee", "reward_type": "item_choice",
    "item_options": [{ "label": "Latte", "item_id": "SKU-1" }], "value_gbp": 3.5, "expiry_days": 30, "enabled": true } ]`,
        curlExample: `curl -X GET {BASE}/api/rewards/<vendor_id> -H "Authorization: Bearer <access_token>"`,
      },
      {
        method: "POST", path: "/api/rewards/:vendor_id", summary: "Create a catalog reward", auth: "jwt",
        description: "Create a reusable reward.",
        requestBody: [
          { name: "name", type: "string", required: true, desc: "Reward name" },
          { name: "reward_type", type: "string", required: false, desc: "free_item | item_choice | percent_discount | cash_value | bonus_points" },
          { name: "item_options", type: "array", required: false, desc: "For item_choice: [{label, item_id}]" },
          { name: "value_gbp", type: "number", required: false, desc: "Cash / item value" },
          { name: "discount_percent", type: "number", required: false, desc: "For percent_discount" },
          { name: "points_value", type: "number", required: false, desc: "For bonus_points" },
          { name: "expiry_days", type: "number", required: false, desc: "Days until the issued reward expires (default 365)" },
          { name: "enabled", type: "boolean", required: false, desc: "Available for attachment" },
        ],
        curlExample: `curl -X POST {BASE}/api/rewards/<vendor_id> \\
  -H "Authorization: Bearer <access_token>" -H "Content-Type: application/json" \\
  -d '{"name":"Free Coffee","reward_type":"item_choice","item_options":[{"label":"Latte","item_id":"SKU-1"}],"value_gbp":3.5}'`,
      },
      {
        method: "PUT", path: "/api/rewards/:vendor_id/:reward_id", summary: "Update a catalog reward", auth: "jwt",
        description: "Partial update (merged then normalized).",
        curlExample: `curl -X PUT {BASE}/api/rewards/<vendor_id>/<reward_id> \\
  -H "Authorization: Bearer <access_token>" -H "Content-Type: application/json" -d '{"value_gbp":4.0}'`,
      },
      {
        method: "PATCH", path: "/api/rewards/:vendor_id/:reward_id/toggle", summary: "Enable / disable a reward", auth: "jwt",
        description: "Flip the enabled flag.",
        curlExample: `curl -X PATCH {BASE}/api/rewards/<vendor_id>/<reward_id>/toggle -H "Authorization: Bearer <access_token>"`,
      },
      {
        method: "DELETE", path: "/api/rewards/:vendor_id/:reward_id", summary: "Delete a catalog reward", auth: "jwt",
        description: "Permanently delete a catalog reward.",
        curlExample: `curl -X DELETE {BASE}/api/rewards/<vendor_id>/<reward_id> -H "Authorization: Bearer <access_token>"`,
      },
    ],
  },
  {
    title: "Issued Rewards",
    description: "Rewards already issued to customers. Auto-issued when point thresholds / milestones / tiers are reached.",
    endpoints: [
      {
        method: "GET", path: "/api/rewards", summary: "List issued rewards", auth: "jwt",
        description: "Paginated list of issued rewards with filters for client and status.",
        requestBody: [
          { name: "client_id", type: "string", required: false, desc: "Filter by vendor" },
          { name: "status", type: "string", required: false, desc: "issued | redeemed | expired" },
          { name: "page", type: "number", required: false, desc: "Page number" },
          { name: "limit", type: "number", required: false, desc: "Items per page" },
        ],
        curlExample: `curl -X GET "{BASE}/api/rewards?status=issued&page=1" -H "Authorization: Bearer <access_token>"`,
      },
      {
        method: "PUT", path: "/api/rewards/:reward_id/redeem", summary: "Manual redeem (admin)", auth: "jwt",
        description: "Manually mark an issued reward as redeemed from the dashboard.",
        curlExample: `curl -X PUT {BASE}/api/rewards/<reward_id>/redeem -H "Authorization: Bearer <access_token>"`,
      },
    ],
  },
  {
    title: "Earning Rules",
    description: "Bonus point / stamp rules (happy hours, double-point days, item boosts) applied automatically at checkout on top of the base reward system.",
    endpoints: [
      {
        method: "GET", path: "/api/earning-rules/:vendor_id", summary: "List earning rules", auth: "jwt",
        description: "Returns all earning rules for a vendor.",
        curlExample: `curl -X GET {BASE}/api/earning-rules/<vendor_id> -H "Authorization: Bearer <access_token>"`,
      },
      {
        method: "POST", path: "/api/earning-rules/:vendor_id", summary: "Create an earning rule", auth: "jwt",
        description: "Create a bonus rule. Effect order at checkout: replace → multiply → add.",
        requestBody: [
          { name: "name", type: "string", required: true, desc: "Rule name" },
          { name: "reward_unit", type: "string", required: false, desc: "percent | flat" },
          { name: "effect_type", type: "string", required: false, desc: "add | multiply | replace" },
          { name: "value", type: "number", required: false, desc: "Bonus value (e.g. 2 for x2, 5 for +5%)" },
          { name: "schedule", type: "object", required: false, desc: "{days, start_date, end_date, ongoing, limit_hours, start_hour, end_hour}" },
          { name: "applies_to", type: "string", required: false, desc: "entire_order | categories | dishes" },
          { name: "apply_to_tiers", type: "string", required: false, desc: "all | selected" },
          { name: "channels", type: "array", required: false, desc: "App, Web, Kiosk, SMS" },
          { name: "notify_customers", type: "boolean", required: false, desc: "Push/SMS notify when active" },
        ],
        curlExample: `curl -X POST {BASE}/api/earning-rules/<vendor_id> \\
  -H "Authorization: Bearer <access_token>" -H "Content-Type: application/json" \\
  -d '{"name":"Double points Sat","effect_type":"multiply","value":2,"schedule":{"days":["Sat"]}}'`,
      },
      {
        method: "PUT", path: "/api/earning-rules/:vendor_id/:rule_id", summary: "Update an earning rule", auth: "jwt",
        description: "Partial update of an earning rule.",
        curlExample: `curl -X PUT {BASE}/api/earning-rules/<vendor_id>/<rule_id> \\
  -H "Authorization: Bearer <access_token>" -H "Content-Type: application/json" -d '{"value":3}'`,
      },
      {
        method: "PATCH", path: "/api/earning-rules/:vendor_id/:rule_id/toggle", summary: "Enable / disable a rule", auth: "jwt",
        description: "Flip the enabled flag.",
        responseExample: `{ "enabled": false }`,
        curlExample: `curl -X PATCH {BASE}/api/earning-rules/<vendor_id>/<rule_id>/toggle -H "Authorization: Bearer <access_token>"`,
      },
      {
        method: "DELETE", path: "/api/earning-rules/:vendor_id/:rule_id", summary: "Delete a rule", auth: "jwt",
        description: "Permanently delete an earning rule.",
        curlExample: `curl -X DELETE {BASE}/api/earning-rules/<vendor_id>/<rule_id> -H "Authorization: Bearer <access_token>"`,
      },
    ],
  },
  {
    title: "Nudge & Notifications",
    description: "AI-generated re-engagement messages plus push notifications to all customers, a group, or specific customers.",
    endpoints: [
      {
        method: "GET", path: "/api/nudge/at-risk", summary: "At-risk customers", auth: "jwt",
        description: "Customers at 80%+ of their reward threshold who haven't transacted in 20+ days — prime re-engagement targets.",
        curlExample: `curl -X GET {BASE}/api/nudge/at-risk -H "Authorization: Bearer <access_token>"`,
      },
      {
        method: "POST", path: "/api/nudge/generate", summary: "Generate nudge message (AI)", auth: "jwt",
        description: "Uses Gemini to generate a personalized SMS-length nudge based on points, inactivity and reward proximity.",
        requestBody: [
          { name: "customer_id", type: "string", required: true, desc: "Target customer ID" },
          { name: "client_id", type: "string", required: true, desc: "Vendor ID for context" },
        ],
        responseExample: `{ "message": "Hey John! You're only 80 points from a free coffee at Coffee House. Pop in this week!" }`,
        curlExample: `curl -X POST {BASE}/api/nudge/generate \\
  -H "Authorization: Bearer <access_token>" -H "Content-Type: application/json" \\
  -d '{"customer_id":"<id>","client_id":"<id>"}'`,
      },
      {
        method: "POST", path: "/api/nudge/send", summary: "Send nudge via FCM webhook", auth: "jwt",
        description: "Sends a nudge message to the vendor's configured FCM webhook and logs it.",
        requestBody: [
          { name: "customer_id", type: "string", required: true, desc: "Target customer ID" },
          { name: "client_id", type: "string", required: true, desc: "Vendor ID" },
          { name: "message", type: "string", required: true, desc: "Message text" },
        ],
        curlExample: `curl -X POST {BASE}/api/nudge/send \\
  -H "Authorization: Bearer <access_token>" -H "Content-Type: application/json" \\
  -d '{"customer_id":"<id>","client_id":"<id>","message":"Your nudge text"}'`,
      },
      {
        method: "POST", path: "/api/push/send/:vendor_id", summary: "Send a push notification", auth: "jwt",
        description: "Send a notification across FCM (web push) + Apple Wallet (pass refresh) + Google Wallet (addMessage). Target all customers, a group, or specific customers. Targeted sends (group/customers) update Google Wallet per recipient.",
        requestBody: [
          { name: "title", type: "string", required: true, desc: "Notification title" },
          { name: "body", type: "string", required: true, desc: "Notification body" },
          { name: "target", type: "string", required: false, desc: "all (default) | group | customers" },
          { name: "group_id", type: "string", required: false, desc: "Required when target=group" },
          { name: "customer_ids", type: "string[]", required: false, desc: "Required when target=customers" },
        ],
        responseExample: `{ "success": true, "sent": 12, "recipients": 12, "fcm": { "sent": 8 }, "apns": { "sent": 4 }, "google": { "updated": 3 } }`,
        curlExample: `curl -X POST {BASE}/api/push/send/<vendor_id> \\
  -H "Authorization: Bearer <access_token>" -H "Content-Type: application/json" \\
  -d '{"title":"VIP Offer","body":"20% off today","target":"customers","customer_ids":["<customer_id>"]}'`,
      },
      {
        method: "POST", path: "/api/push/subscribe", summary: "Register a web-push token", auth: "public",
        description: "Called by the customer portal after the browser grants notification permission.",
        requestBody: [
          { name: "token", type: "string", required: true, desc: "FCM device token" },
          { name: "customer_id", type: "string", required: true, desc: "Customer ID" },
          { name: "vendor_id", type: "string", required: true, desc: "Vendor ID" },
        ],
        curlExample: `curl -X POST {BASE}/api/push/subscribe \\
  -H "Content-Type: application/json" -d '{"token":"<fcm_token>","customer_id":"<id>","vendor_id":"<id>"}'`,
      },
    ],
  },
  {
    title: "Customer Portal (Public)",
    description: "Unauthenticated endpoints powering the public QR portal and customer self-registration.",
    endpoints: [
      {
        method: "GET", path: "/api/portal/client-info", summary: "Get business info", auth: "public",
        description: "Public business info for the portal header. Accepts vendor id, slug or custom client_id.",
        requestBody: [{ name: "client_id", type: "string", required: true, desc: "Vendor id / slug / custom client_id (query param)" }],
        curlExample: `curl -X GET "{BASE}/api/portal/client-info?client_id=<id-or-slug>"`,
      },
      {
        method: "POST", path: "/api/portal/signup", summary: "Customer self-registration", auth: "public",
        description: "Public customer sign-up for a vendor (the /:slug/join page). Captures name, phone, optional email, DOB and T&C consent. Sends a welcome email when an email is provided.",
        requestBody: [
          { name: "client_id", type: "string", required: true, desc: "Vendor id / slug" },
          { name: "name", type: "string", required: true, desc: "Customer name" },
          { name: "mobile_number", type: "string", required: true, desc: "Phone (with country code)" },
          { name: "email", type: "string", required: false, desc: "Email (enables welcome + future emails)" },
          { name: "date_of_birth", type: "string", required: false, desc: "YYYY-MM-DD" },
          { name: "terms_accepted", type: "boolean", required: true, desc: "Must be true" },
        ],
        responseExample: `{ "success": true, "message": "Welcome to Coffee House!", "customer_id": "...", "existing": false }`,
        curlExample: `curl -X POST {BASE}/api/portal/signup \\
  -H "Content-Type: application/json" \\
  -d '{"client_id":"coffee-house","name":"John Doe","mobile_number":"+447700900000","email":"john@email.com","terms_accepted":true}'`,
      },
      {
        method: "GET", path: "/api/portal/lookup", summary: "Customer self-serve lookup", auth: "public",
        description: "Customer looks up their own balance, tier, active rewards and recent transactions.",
        requestBody: [
          { name: "identifier", type: "string", required: true, desc: "Customer email or mobile (query param)" },
          { name: "client_id", type: "string", required: true, desc: "Vendor id / slug (query param)" },
        ],
        curlExample: `curl -X GET "{BASE}/api/portal/lookup?identifier=john@email.com&client_id=coffee-house"`,
      },
      {
        method: "POST", path: "/api/portal/wallet-pass", summary: "Generate a wallet pass", auth: "public",
        description: "Generates the Apple/Google Wallet pass for a customer and returns add-to-wallet links.",
        requestBody: [
          { name: "identifier", type: "string", required: true, desc: "Customer email or mobile" },
          { name: "client_id", type: "string", required: true, desc: "Vendor id / slug" },
        ],
        curlExample: `curl -X POST {BASE}/api/portal/wallet-pass \\
  -H "Content-Type: application/json" -d '{"identifier":"john@email.com","client_id":"coffee-house"}'`,
      },
      {
        method: "POST", path: "/api/portal/remove-pass", summary: "Void a wallet pass", auth: "public",
        description: "Marks a customer's wallet pass as voided so it can be removed.",
        curlExample: `curl -X POST {BASE}/api/portal/remove-pass \\
  -H "Content-Type: application/json" -d '{"identifier":"john@email.com","client_id":"coffee-house"}'`,
      },
    ],
  },
  {
    title: "Wallet Settings (Admin)",
    description: "Platform-level Apple & Google Wallet credentials used to sign and update passes.",
    endpoints: [
      {
        method: "GET", path: "/api/settings/wallet", summary: "Get wallet credential status", auth: "jwt",
        description: "Admin only. Returns masked Apple/Google wallet credential status.",
        curlExample: `curl -X GET {BASE}/api/settings/wallet -H "Authorization: Bearer <admin_token>"`,
      },
      {
        method: "PUT", path: "/api/settings/wallet/apple", summary: "Set Apple Wallet credentials", auth: "jwt",
        description: "Admin only. Save Apple Team ID, Pass Type ID, org name, .p12 cert + password.",
        curlExample: `curl -X PUT {BASE}/api/settings/wallet/apple -H "Authorization: Bearer <admin_token>" -H "Content-Type: application/json" -d '{...}'`,
      },
      {
        method: "PUT", path: "/api/settings/wallet/google", summary: "Set Google Wallet credentials", auth: "jwt",
        description: "Admin only. Save Google Issuer ID + service account JSON.",
        curlExample: `curl -X PUT {BASE}/api/settings/wallet/google -H "Authorization: Bearer <admin_token>" -H "Content-Type: application/json" -d '{...}'`,
      },
      {
        method: "DELETE", path: "/api/settings/wallet/:platform", summary: "Clear wallet credentials", auth: "jwt",
        description: "Admin only. Clears credentials for a platform (apple | google).",
        curlExample: `curl -X DELETE {BASE}/api/settings/wallet/apple -H "Authorization: Bearer <admin_token>"`,
      },
    ],
  },
  {
    title: "Analytics",
    description: "Dashboard analytics and chart data for the superadmin overview.",
    endpoints: [
      {
        method: "GET", path: "/api/analytics/stats", summary: "Global statistics", auth: "jwt",
        description: "Platform-wide counts: vendors, customers, transactions, points issued, rewards status and revenue.",
        responseExample: `{ "total_vendors": 12, "total_customers": 854, "shadow_profiles": 120, "total_transactions": 5400,
  "total_points_issued": 234500, "rewards_issued": 85, "rewards_redeemed": 42, "total_revenue_gbp": 52300.00 }`,
        curlExample: `curl -X GET {BASE}/api/analytics/stats -H "Authorization: Bearer <access_token>"`,
      },
      {
        method: "GET", path: "/api/analytics/chart-data", summary: "30-day chart data", auth: "jwt",
        description: "Daily aggregated points, spend and transaction counts for the last 30 days.",
        curlExample: `curl -X GET {BASE}/api/analytics/chart-data -H "Authorization: Bearer <access_token>"`,
      },
      {
        method: "GET", path: "/api/analytics/top-vendors", summary: "Top 5 vendors by points", auth: "jwt",
        description: "Top 5 vendors ranked by total points earned across their customers.",
        responseExample: `[ { "vendor_id": "...", "vendor_name": "Coffee House", "total_points": 45200, "tx_count": 820 } ]`,
        curlExample: `curl -X GET {BASE}/api/analytics/top-vendors -H "Authorization: Bearer <access_token>"`,
      },
    ],
  },
];

export default function ApiDocsPage() {
  const [search, setSearch] = useState("");

  const filtered = ENDPOINT_GROUPS.map(group => {
    const endpoints = group.endpoints.filter(ep =>
      !search ||
      ep.path.toLowerCase().includes(search.toLowerCase()) ||
      ep.summary.toLowerCase().includes(search.toLowerCase()) ||
      ep.method.toLowerCase().includes(search.toLowerCase()) ||
      group.title.toLowerCase().includes(search.toLowerCase())
    );
    return { ...group, endpoints };
  }).filter(g => g.endpoints.length > 0);

  const totalEndpoints = ENDPOINT_GROUPS.reduce((sum, g) => sum + g.endpoints.length, 0);

  return (
    <div data-testid="api-docs-page" className="max-w-5xl mx-auto">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-ink tracking-tight" data-testid="api-docs-title">
          API Reference
        </h1>
        <p className="text-sm text-ink/50 mt-1">
          {totalEndpoints} endpoints across {ENDPOINT_GROUPS.length} groups. All endpoints are prefixed with your base URL.
        </p>
      </div>

      {/* Authentication overview (JWT + API Key) */}
      <AuthOverview />

      {/* Auth legend + Search */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <AuthBadge type="jwt" />
          <span className="text-xs text-ink/40">Dashboard token</span>
          <span className="text-ink/30">|</span>
          <AuthBadge type="apikey" />
          <span className="text-xs text-ink/40">Vendor POS key</span>
          <span className="text-ink/30">|</span>
          <AuthBadge type="public" />
          <span className="text-xs text-ink/40">No auth needed</span>
        </div>
        <div className="relative w-full sm:w-64">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
          <Input
            data-testid="api-docs-search"
            placeholder="Search endpoints..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm"
          />
        </div>
      </div>

      {/* Endpoint Groups */}
      <div className="space-y-6">
        {filtered.map((group) => (
          <div key={group.title} data-testid={`api-group-${group.title.toLowerCase().replace(/ /g, "-")}`} className="bg-white border border-ink/10 rounded-md shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-ink/5 bg-surface/50">
              <h2 className="text-sm font-semibold text-ink tracking-tight">{group.title}</h2>
              <p className="text-xs text-ink/50 mt-0.5">{group.description}</p>
            </div>
            <Accordion type="multiple" className="px-2">
              {group.endpoints.map((ep, idx) => (
                <AccordionItem key={idx} value={`${group.title}-${idx}`} className="border-b border-ink/5 last:border-0">
                  <AccordionTrigger className="px-3 py-3 hover:no-underline hover:bg-surface/50 rounded-sm" data-testid={`endpoint-trigger-${ep.method.toLowerCase()}-${ep.path.replace(/[/:]/g, "-")}`}>
                    <div className="flex items-center gap-3 w-full">
                      <MethodBadge method={ep.method} />
                      <code className="text-xs font-mono text-ink/80 flex-1 text-left">{ep.path}</code>
                      <AuthBadge type={ep.auth} />
                      <span className="text-xs text-ink/40 hidden sm:inline">{ep.summary}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pb-4">
                    <div className="space-y-4">
                      <p className="text-sm text-ink/60 leading-relaxed">{ep.description}</p>

                      {ep.requestBody && ep.requestBody.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-ink tracking-wide uppercase mb-2">
                            {ep.method === "GET" ? "Query Parameters" : "Request Body"}
                          </h4>
                          <div className="bg-surface border border-ink/5 rounded-md p-3">
                            {ep.requestBody.map((f, i) => (
                              <Field key={i} {...f} />
                            ))}
                          </div>
                        </div>
                      )}

                      {ep.responseExample && (
                        <div>
                          <h4 className="text-xs font-semibold text-ink tracking-wide uppercase mb-2">Response</h4>
                          <CopyBlock text={ep.responseExample} />
                        </div>
                      )}

                      {ep.curlExample && (
                        <div>
                          <h4 className="text-xs font-semibold text-ink tracking-wide uppercase mb-2">Example (cURL)</h4>
                          <CopyBlock text={ep.curlExample.replace(/\{BASE\}/g, window.location.origin)} />
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="text-center py-16 text-ink/40 text-sm" data-testid="api-docs-empty">
            No endpoints match "{search}"
          </div>
        )}
      </div>
    </div>
  );
}
